/**
 * Custom themed date / date-time picker for a SINGLE instant. Replaces the
 * native `<input type="datetime-local|date">` so the popup is OUR DOM (the
 * native one can't be styled beyond color-scheme/accent-color).
 *
 * Controlled by a string in the same format the native inputs use, so it's
 * a drop-in: `YYYY-MM-DDTHH:mm` (datetime) or `YYYY-MM-DD` (date). The
 * trigger is a <button> styled by the host via `className`; the calendar is
 * a portal popover (same pattern as ColorPicker) so it escapes overflow
 * clipping and themes fully.
 *
 * For a From → To window use `DateRangePicker` — same shell, one calendar,
 * round-trip selection.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { nowDateParts } from "@/utils/chartTime";
import { useAnchoredPopover } from "./useAnchoredPopover";
import { TimeFields } from "./TimeFields";
import {
  addMonths,
  dateKey,
  dayKey,
  display,
  format,
  monthCells,
  MONTHS_LONG,
  parse,
  WEEKDAYS,
  type Mode,
  type Parts,
} from "./dateParts";
import styles from "./DateTimePicker.module.css";

interface Props {
  value: string;
  onChange: (value: string) => void;
  mode?: Mode;
  ariaLabel?: string;
  /** Applied to the trigger button so the host controls its look. */
  className?: string;
  placeholder?: string;
  /** Reference zone for the "today" dot + the month the popover opens on when
   *  there's no value. The emitted value string is zone-agnostic (literal
   *  wall-clock) — this only affects which day reads as "today". Use "utc"
   *  where the value is interpreted as UTC (analysis range, weekly
   *  behaviour); "local" (default) for broker-local pickers (order expiry). */
  zone?: "local" | "utc";
}

const POPOVER_W = 262;

export function DateTimePicker({
  value,
  onChange,
  mode = "datetime",
  ariaLabel,
  className,
  placeholder = "Pick a date",
  zone = "local",
}: Props) {
  const parsed = parse(value);
  const [open, setOpen] = useState(false);

  // "Today" in the picker's reference zone — drives only the today-dot and
  // the default-open month, never the emitted value (which stays literal).
  // The zone branch lives in chartTime (single time source).
  const today = nowDateParts(zone);
  const [view, setView] = useState<{ y: number; m: number }>(() =>
    parsed ? { y: parsed.year, m: parsed.month } : { y: today.y, m: today.m },
  );

  const { triggerRef, popRef, coords } = useAnchoredPopover(
    open,
    () => setOpen(false),
    POPOVER_W,
    view,
  );

  // Snap the calendar to the selected month (or today) each time it opens.
  useEffect(() => {
    if (!open) return;
    const p = parse(value);
    const n = nowDateParts(zone);
    setView(p ? { y: p.year, m: p.month } : { y: n.y, m: n.m });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const base = (): Parts =>
    parsed ?? {
      year: today.y,
      month: today.m,
      day: today.d,
      hour: 0,
      minute: 0,
    };

  const pickDay = (d: Date) => {
    onChange(
      format(
        {
          ...base(),
          year: d.getFullYear(),
          month: d.getMonth(),
          day: d.getDate(),
        },
        mode,
      ),
    );
    setView({ y: d.getFullYear(), m: d.getMonth() });
    if (mode === "date") setOpen(false);
  };

  const setTime = (hour: number, minute: number) => {
    onChange(
      format(
        {
          ...base(),
          hour: Math.max(0, Math.min(23, hour || 0)),
          minute: Math.max(0, Math.min(59, minute || 0)),
        },
        mode,
      ),
    );
  };

  const todayKey = dayKey({ year: today.y, month: today.m, day: today.d });
  const selKey = parsed ? dayKey(parsed) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {display(parsed, mode, placeholder)}
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={popRef}
              className={styles.popover}
              role="dialog"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                width: POPOVER_W,
              }}
            >
              <div className={styles.head}>
                <button
                  type="button"
                  className={styles.nav}
                  onClick={() => setView((v) => addMonths(v, -1))}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <div className={styles.monthLabel}>
                  {MONTHS_LONG[view.m]} {view.y}
                </div>
                <button
                  type="button"
                  className={styles.nav}
                  onClick={() => setView((v) => addMonths(v, 1))}
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>

              <div className={styles.weekRow}>
                {WEEKDAYS.map((w) => (
                  <span key={w} className={styles.weekday}>
                    {w}
                  </span>
                ))}
              </div>

              <div className={styles.grid}>
                {monthCells(view).map((d, i) => {
                  const k = dateKey(d);
                  const sel = k === selKey;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={[
                        styles.day,
                        d.getMonth() === view.m ? "" : styles.dayOut,
                        sel ? styles.daySel : "",
                        k === todayKey && !sel ? styles.dayToday : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => pickDay(d)}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              {mode === "datetime" ? (
                <div className={styles.timeRow}>
                  <span className={styles.timeLabel}>Time</span>
                  <TimeFields
                    hour={parsed?.hour ?? 0}
                    minute={parsed?.minute ?? 0}
                    onChange={setTime}
                  />
                  <button
                    type="button"
                    className={styles.doneBtn}
                    onClick={() => setOpen(false)}
                  >
                    Done
                  </button>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
